import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Crown, Sparkles, Lock, CalendarClock, CreditCard, Receipt, RefreshCw, Compass, Layers, Footprints,
} from "lucide-react";
import { toast } from "sonner";

const PLAN_LABEL = { mensile: "Mensile", annuale: "Annuale", mecenate: "Mecenate", trial: "Prova gratuita" };

export default function Account() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [sub, setSub] = useState(null);
  const [payments, setPayments] = useState(null);
  const [maestro, setMaestro] = useState(null);
  const [savingMode, setSavingMode] = useState(false);

  const setMode = async (m) => {
    if (m === user?.modalita) return;
    setSavingMode(true);
    try {
      await api.post("/me/modalita", { modalita: m });
      await refreshUser();
      toast.success(m === "mathema" ? "Modalità Máthema attivata" : "Modalità Compendium attivata");
    } catch {
      toast.error("Non è stato possibile cambiare modalità.");
    } finally {
      setSavingMode(false);
    }
  };

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => setSub({ has_access: false, plan: null, access_until: null, days_left: 0, is_trial: false, plan_nome: null }));
    api.get("/payments/history").then((r) => setPayments(r.data.payments)).catch(() => setPayments([]));
    api.get("/onboarding/maestro").then((r) => setMaestro(r.data.maestro_guida)).catch(() => setMaestro(null));
  }, []);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  if (!sub)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
        </div>
      </div>
    );

  const active = sub.has_access;
  const trial = sub.is_trial;
  const isAdmin = user?.tier === "admin";
  const isSuper = user?.tier === "super";
  const isPrivileged = isAdmin || isSuper;
  const planName = isAdmin ? "Amministratore" : (sub.plan_nome || PLAN_LABEL[sub.plan] || "Nessun posto attivo");

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="h-6 w-6 text-primary" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl font-bold">Il tuo posto in Bottega</h1>
        </div>
        <p className="text-muted-foreground mb-8">Gestisci il tuo accesso alla Bottega di Filosofia.</p>

        {/* Status card */}
        <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="subscription-status-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Piano attuale</p>
              <div className="flex items-center gap-2">
                <h2 className="font-serif text-2xl font-bold" data-testid="current-plan-name">
                  {planName}
                </h2>
                {user?.mecenate && (
                  <Badge className="rounded-full bg-accent text-accent-foreground" data-testid="mecenate-badge">
                    <Crown className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Mecenate
                  </Badge>
                )}
                {isPrivileged ? (
                  <Badge className="rounded-full bg-accent text-accent-foreground">
                    <Crown className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> {isAdmin ? "Accesso pieno" : "Accesso riservato"}
                  </Badge>
                ) : active ? (
                  trial ? (
                    <Badge variant="outline" className="rounded-full border-accent/40">
                      <Sparkles className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Prova
                    </Badge>
                  ) : (
                    <Badge className="rounded-full bg-accent text-accent-foreground">
                      <Crown className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Attivo
                    </Badge>
                  )
                ) : (
                  <Badge variant="outline" className="rounded-full border-border text-muted-foreground">
                    <Lock className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Scaduto
                  </Badge>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5 justify-end">
                <CalendarClock className="h-3.5 w-3.5" strokeWidth={1.5} /> {isAdmin ? "Durata" : (active ? "Scade il" : "Scaduto il")}
              </p>
              <p className="font-serif text-xl font-bold" data-testid="access-expiry">{isAdmin ? "Sine die" : fmtDate(sub.access_until)}</p>
              {!isAdmin && active && <p className="text-sm text-muted-foreground">{sub.days_left} giorni rimanenti</p>}
            </div>
          </div>

          {!isPrivileged && (
          <div className="mt-6 flex gap-3 flex-wrap">
            <Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="manage-subscribe-btn"
              onClick={() => navigate("/abbonamento")}>
              {active && !trial ? <><RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Rinnova o cambia piano</>
                : <><Crown className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {active ? "Passa a un piano completo" : "Riattiva l'abbonamento"}</>}
            </Button>
          </div>
          )}
        </div>

        {!active && (
          <div className="bg-secondary/60 border border-border rounded-md p-4 mb-6 text-sm text-muted-foreground" data-testid="account-paywall-note">
            Il tuo accesso è terminato. Puoi comunque vedere la Bottega e la prima lectio di ogni corso.
            Abbonati per sbloccare tutte le lectiones e gli esami.
          </div>
        )}

        {/* Maestro-guida */}
        <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="maestro-guida-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
                <Compass className="h-3.5 w-3.5" strokeWidth={1.5} /> Il tuo Maestro-guida
              </p>
              {maestro ? (
                <>
                  <h2 className="font-serif text-2xl font-bold" data-testid="maestro-guida-nome">{maestro.maestro}</h2>
                  <p className="text-sm text-muted-foreground italic mt-1">«{maestro.citazione}»</p>
                  <p className="text-sm text-muted-foreground mt-2">Corso suggerito: <span className="font-medium text-foreground">{maestro.corso_nome}</span></p>
                </>
              ) : (
                <p className="text-muted-foreground mt-1">Non hai ancora scoperto il tuo Maestro-guida.</p>
              )}
            </div>
            <Button variant="outline" className="rounded-sm" data-testid="maestro-quiz-btn"
              onClick={() => navigate(maestro ? "/onboarding?ripeti=1" : "/onboarding")}>
              <Compass className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {maestro ? "Rifai il test" : "Scopri il tuo Maestro"}
            </Button>
          </div>
          {maestro && (
            <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90 mt-4" data-testid="maestro-go-course-btn"
              onClick={() => navigate(`/corso/${maestro.course_id}`)}>
              Vai al corso di {maestro.maestro}
            </Button>
          )}
        </div>

        {/* Modalità di studio (solo account standard) */}
        {user?.tier === "normale" && (
          <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="modalita-card">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" strokeWidth={1.5} /> Modalità di studio
            </p>
            <p className="text-sm text-muted-foreground mb-4">Scegli come sbloccare le lectiones. Puoi cambiare quando vuoi.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { key: "compendium", icon: Layers, titolo: "Compendium", desc: "Tutte le lectiones sbloccate da subito, nel tuo ordine." },
                { key: "mathema", icon: Footprints, titolo: "Máthema", desc: "Sblocco a tappe: supera la Prova (max 2 al giorno) per la lectio successiva." },
              ].map(({ key, icon: Icon, titolo, desc }) => {
                const attiva = (user?.modalita || "compendium") === key;
                return (
                  <button key={key} onClick={() => setMode(key)} disabled={savingMode}
                    data-testid={`modalita-${key}-btn`}
                    className={`text-left p-5 rounded-lg border transition-all disabled:opacity-60 ${
                      attiva ? "border-primary bg-primary/[0.06] ring-1 ring-primary/30" : "border-border bg-card hover:border-primary/50"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`h-6 w-6 ${attiva ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
                      {attiva && <Badge className="rounded-full bg-primary text-primary-foreground text-[10px] px-2 py-0">Attiva</Badge>}
                    </div>
                    <p className="font-serif text-lg font-bold">{titolo}</p>
                    <p className="text-sm text-muted-foreground mt-1">{desc}</p>
                  </button>
                );
              })}
            </div>
            {savingMode && <div className="pt-3 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" strokeWidth={1.5} /></div>}
          </div>
        )}

        {/* Payment history */}
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl font-bold">Storico pagamenti</h2>
        </div>

        {payments === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={1.5} /></div>
        ) : payments.length === 0 ? (
          <div className="bg-card border border-border rounded-md p-8 text-center" data-testid="no-payments">
            <Receipt className="h-9 w-9 mx-auto text-muted-foreground/40 mb-3" strokeWidth={1.5} />
            <p className="text-muted-foreground">Nessun pagamento effettuato.</p>
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-3 bg-secondary/60 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <div className="col-span-5">Piano</div>
              <div className="col-span-4">Data</div>
              <div className="col-span-3 text-right">Importo</div>
            </div>
            {payments.map((p, i) => (
              <div key={`${p.date}-${p.plan_id}`} data-testid={`payment-row-${i}`}
                className={`grid grid-cols-2 sm:grid-cols-12 gap-3 px-5 py-4 items-center ${i > 0 ? "border-t border-border" : ""}`}>
                <div className="col-span-1 sm:col-span-5 font-medium">Abbonamento {p.plan_nome}</div>
                <div className="col-span-1 sm:col-span-4 text-sm text-muted-foreground">{fmtDate(p.date)}</div>
                <div className="col-span-2 sm:col-span-3 sm:text-right">
                  <span className="font-serif font-bold">€{Number(p.amount).toFixed(2).replace(".", ",")}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
